import {Widget, WidgetType} from '@dv5/domain'
import React, {FC, useCallback} from 'react'
import {canvasMoveService, WidgetResize} from '../../services/canvasMoveService'
import {AlignWidgetButton} from './AlignWidgetButton'
import {ShapeWidgetPropertiesPanel} from './ShapeWidgetPropertiesPanel'
import {StickyNotePropertiesPanel} from './StickyNotePropertiesPanel'
import {TextWidgetPropertiesPanel} from './TextWidgetPropertiesPanel'
import {
    IconAlignBottom,
    IconAlignCenterH,
    IconAlignCenterV,
    IconAlignLeft,
    IconAlignRight,
    IconAlignTop,
    IconDistributeH,
    IconDistributeV,
    IconSameHeight,
    IconSameWidth,
} from './AlignWidgetIcons'
import './AlignWidgetsBar.less'

interface Props {
    canvasId: string
    widgets: Widget[]
    selectedIds: string[]
    onClose: () => void
}

type AlignAction =
    | 'align-left'
    | 'align-center-h'
    | 'align-right'
    | 'align-top'
    | 'align-center-v'
    | 'align-bottom'
    | 'distribute-h'
    | 'distribute-v'
    | 'same-width'
    | 'same-height'

export const AlignWidgetsBar: FC<Props> = ({
    canvasId,
    widgets,
    selectedIds,
    onClose,
}) => {
    const selectedWidgets = widgets.filter(w => selectedIds.includes(w.id))
    const allStickyNotes = selectedWidgets.length >= 2 &&
        selectedWidgets.every(w => w.type === WidgetType.STICKY_NOTE)
    const allTextWidgets = selectedWidgets.length >= 2 &&
        selectedWidgets.every(w => w.type === WidgetType.TEXT_WIDGET)
    const allShapes = selectedWidgets.length >= 2 &&
        selectedWidgets.every(w => w.type === WidgetType.SHAPE)

    const handleAlign = useCallback((action: AlignAction) => {
        if (selectedWidgets.length < 2) {
            return
        }

        const minLeft = Math.min(...selectedWidgets.map(w => w.left))
        const maxRight = Math.max(...selectedWidgets.map(w => w.left + w.width))
        const minTop = Math.min(...selectedWidgets.map(w => w.top))
        const maxBottom = Math.max(...selectedWidgets.map(w => w.top + w.height))

        let positions: Array<{id: string; left: number; top: number}> | null = null
        let resizes: Array<WidgetResize> | null = null

        switch (action) {
            case 'align-left':
                positions = selectedWidgets.map(w => ({id: w.id, left: minLeft, top: w.top}))
                break
            case 'align-center-h':
                positions = selectedWidgets.map(w => ({
                    id: w.id,
                    left: Math.round((minLeft + maxRight) / 2 - w.width / 2),
                    top: w.top,
                }))
                break
            case 'align-right':
                positions = selectedWidgets.map(w => ({id: w.id, left: maxRight - w.width, top: w.top}))
                break
            case 'align-top':
                positions = selectedWidgets.map(w => ({id: w.id, left: w.left, top: minTop}))
                break
            case 'align-center-v':
                positions = selectedWidgets.map(w => ({
                    id: w.id,
                    left: w.left,
                    top: Math.round((minTop + maxBottom) / 2 - w.height / 2),
                }))
                break
            case 'align-bottom':
                positions = selectedWidgets.map(w => ({id: w.id, left: w.left, top: maxBottom - w.height}))
                break
            case 'distribute-h': {
                const sorted = [...selectedWidgets].sort((a, b) => a.left - b.left)
                const totalWidth = sorted.reduce((sum, w) => sum + w.width, 0)
                const gap = (maxRight - minLeft - totalWidth) / (sorted.length - 1)
                let currentLeft = minLeft
                positions = sorted.map(w => {
                    const pos = {id: w.id, left: Math.round(currentLeft), top: w.top}
                    currentLeft += w.width + gap
                    return pos
                })
                break
            }
            case 'distribute-v': {
                const sorted = [...selectedWidgets].sort((a, b) => a.top - b.top)
                const totalHeight = sorted.reduce((sum, w) => sum + w.height, 0)
                const gap = (maxBottom - minTop - totalHeight) / (sorted.length - 1)
                let currentTop = minTop
                positions = sorted.map(w => {
                    const pos = {id: w.id, left: w.left, top: Math.round(currentTop)}
                    currentTop += w.height + gap
                    return pos
                })
                break
            }
            case 'same-width': {
                const maxWidth = Math.max(...selectedWidgets.map(w => w.width))
                resizes = selectedWidgets.map(w => ({
                    id: w.id,
                    left: w.left,
                    top: w.top,
                    width: maxWidth,
                    height: w.height,
                }))
                break
            }
            case 'same-height': {
                const maxHeight = Math.max(...selectedWidgets.map(w => w.height))
                resizes = selectedWidgets.map(w => ({
                    id: w.id,
                    left: w.left,
                    top: w.top,
                    width: w.width,
                    height: maxHeight,
                }))
                break
            }
        }

        if (positions) {
            canvasMoveService.moveWidgets(canvasId, positions)
        }
        if (resizes) {
            canvasMoveService.resizeWidgets(canvasId, resizes)
        }
    }, [selectedWidgets, canvasId])

    return (
        <div className='align-widgets-bar'>
            <div className='align-widgets-bar__columns'>
                {(allStickyNotes || allTextWidgets || allShapes) && (
                    <>
                        <div className='align-widgets-bar__column'>
                            {allStickyNotes && (
                                <StickyNotePropertiesPanel
                                    canvasId={canvasId}
                                    widgets={selectedWidgets}
                                />
                            )}
                            {allTextWidgets && (
                                <TextWidgetPropertiesPanel
                                    canvasId={canvasId}
                                    widgets={selectedWidgets}
                                />
                            )}
                            {allShapes && (
                                <ShapeWidgetPropertiesPanel
                                    canvasId={canvasId}
                                    widgets={selectedWidgets}
                                />
                            )}
                        </div>
                        <div className='align-widgets-bar__column-divider' />
                    </>
                )}

                <div className='align-widgets-bar__column'>
                    <div className='align-widgets-bar__row'>
                        <AlignWidgetButton action='align-top' title='Align top' onAction={handleAlign}>
                            <IconAlignTop />
                        </AlignWidgetButton>
                        <AlignWidgetButton action='align-center-v' title='Align center vertically' onAction={handleAlign}>
                            <IconAlignCenterV />
                        </AlignWidgetButton>
                        <AlignWidgetButton action='align-bottom' title='Align bottom' onAction={handleAlign}>
                            <IconAlignBottom />
                        </AlignWidgetButton>
                    </div>
                    <div className='align-widgets-bar__row'>
                        <AlignWidgetButton action='align-left' title='Align left' onAction={handleAlign}>
                            <IconAlignLeft />
                        </AlignWidgetButton>
                        <AlignWidgetButton action='align-center-h' title='Align center horizontally' onAction={handleAlign}>
                            <IconAlignCenterH />
                        </AlignWidgetButton>
                        <AlignWidgetButton action='align-right' title='Align right' onAction={handleAlign}>
                            <IconAlignRight />
                        </AlignWidgetButton>
                    </div>
                </div>

                <div className='align-widgets-bar__column-divider' />

                <div className='align-widgets-bar__column'>
                    <div className='align-widgets-bar__row'>
                        <AlignWidgetButton action='distribute-h' title='Distribute horizontally' onAction={handleAlign}>
                            <IconDistributeH />
                        </AlignWidgetButton>
                        <AlignWidgetButton action='distribute-v' title='Distribute vertically' onAction={handleAlign}>
                            <IconDistributeV />
                        </AlignWidgetButton>
                    </div>
                    <div className='align-widgets-bar__row'>
                        <AlignWidgetButton action='same-width' title='Same width' onAction={handleAlign}>
                            <IconSameWidth />
                        </AlignWidgetButton>
                        <AlignWidgetButton action='same-height' title='Same height' onAction={handleAlign}>
                            <IconSameHeight />
                        </AlignWidgetButton>
                    </div>
                </div>

                <button
                    className='align-widgets-bar__close'
                    onClick={onClose}
                    aria-label='Close align bar'
                    type='button'
                >
                    ×
                </button>
            </div>
        </div>
    )
}
